const debug = require('debug')('mtk:react:user');

import React, { useMemo } from 'react';

import {
  modes,
  roles,
  roleLabels,
  noPasswordRoles,
  userRoleSpecifications,
  userPreferenceOptions,
} from '../constants';
import { can } from '../can';

import api from '../utils/api';
import { useForm } from '../utils/form';
import validators from '../utils/validate';

import PageHeader from '../components/common/PageHeader';
import Button from '../components/common/Button';

import Input from '../components/common/Input';
import Toggle from '../components/common/Toggle';
import Select, { indexToOptions, objectToOptions } from '../components/common/Select';

import grid from '../styles/grid.css';
import css from './UserNew.css';

let newUserFields = {
  name: ['text', true],
  email: ['email', true],
  role: ['text', true],
  clientName: ['text'],
  password: [
    'password',
    null,
    {
      required: (v, values) => {
        return !noPasswordRoles.has(values.get('role'));
      },
      validate: (v, values) => {
        if (!noPasswordRoles.has(values.get('role'))) {
          validators.password(v);
        } else {
          return null;
        }
      },
    },
  ],
  password_confirm: [
    'password',
    null,
    {
      required: (v, values) => !noPasswordRoles.has(values.get('role')),
      validate: (v, values) => (v == values.get('password') ? null : 'las contraseñas deben coincidir'),
    },
  ],
  phone: ['tel'],
  clientId: ['number'],
  status: ['text'],
  nationalId: ['rut'],
  preferences: userPreferenceOptions.reduce((fields, option) => ({ ...fields, [option.name]: [option.type] }), {}),
};

const UserNew = ({ match, history }) => {
  const { user } = api.auth();
  const { item, actions } = api.users.newItem({ history });
  const { item: client, actions: clientActions } = api.clients.item(null, null, { lazy: true });
  const { actions: clientIndexActions } = api.clients.index(null, { lazy: true });

  const form = useForm(newUserFields, item.data);
  const clientPreferences = client.data ? client.data.get('preferences').toJS() : {};
  const clientMedicalEnabled = clientPreferences.medical === true;
  const isAdmin = user.role == 'admin';
  const readOnly = !can(user, 'update', 'users', item.data);
  const name = form.getValue('name');

  useMemo(() => {
    if (user.clientId) clientActions.get(null, null, user.clientId);
  }, [user.clientId]);

  const getClients = async (search) => {
    const index = await clientIndexActions.get({ search, perPage: 50 });
    const options = indexToOptions(index, { value: 'id', label: 'name' });
    return options;
  };

  const onClientChange = (name, value) => {
    form.setValue('clientId', value);
    clientActions.get(null, null, value);
  };

  const handleCreate = async () => {
    if (form.validate(null, true)) {
      let values = form.getValues();
      if (user.clientId) values = values.set('clientId', user.clientId);
      actions.create(values);
    }
  };

  const roleLabelOptions = {
    ...roleLabels.obj,
    ...(clientMedicalEnabled ? { noAccess: 'Paciente' } : {}),
  };
  if (clientMedicalEnabled) delete roleLabelOptions[roles('resident')];
  let roleOptions = objectToOptions(roleLabelOptions);

  if (!isAdmin) {
    roleOptions = roleOptions.filter((option) => option.value !== roles('admin'));
    if (user.clientMode !== modes('housing')) {
      roleOptions = roleOptions.filter((option) => option.value !== roles('resident'));
    }
  }

  const selectedRole = form.getValue('role');
  const roleSpecification = userRoleSpecifications[selectedRole];
  const minimumFields = roleSpecification ? [...roleSpecification.requiredFields] : [];
  if (isAdmin && !user.clientId && selectedRole !== roles('admin')) minimumFields.push('cliente');
  const createButton = (
    <Button
      className={css.createUserButton}
      disabled={item.loading}
      loading={item.loading}
      onClick={handleCreate}>
      {item.loading ? 'Creando usuario' : 'Crear usuario'}
    </Button>
  );

  return (
    <main className={css.root}>
      <PageHeader section="users" title={`Nuevo usuario ${name ? `: ${name}` : ''}`} {...item}>
        {item.loading && <small className={css.createUserMessage}>Creando usuario...</small>}
        {createButton}
      </PageHeader>

      <div className={grid.grid}>
        <section className={grid.half}>
          <div className={css.formPanel}>
            <h3 className={css.panelTitle}>Cuenta y acceso</h3>
            {isAdmin && !user.clientId && (
              <Select
                label="Cliente"
                name="clients"
                onChange={onClientChange}
                loadOptions={getClients}
                form={form}
                noOptionsMessage={(a) => {
                  if (a.inputValue) {
                    return `No hay clientes que contengan "${a.inputValue}"`;
                  }
                  return 'No hay clientes.';
                }}
              />
            )}

            <Select
              label="Rol"
              name="role"
              options={roleOptions}
              isClearable={false}
              readOnly={readOnly}
              form={form}
              note={
                roleSpecification
                  ? `Acceso: ${roleSpecification.access}.`
                  : null
              }
            />
            <Input name="name" label="Nombre" form={form} />
            <Input
              name="email"
              label="E-Mail"
              placeholder="user@maletek.cl"
              type="email"
              readOnly={readOnly}
              form={form}
            />
          </div>
        </section>
        <section className={grid.half}>
          <div className={css.formPanel}>
            <h3 className={css.panelTitle}>Datos personales y seguridad</h3>
            <Input name="phone" placeholder="+56 9 9999 9999" label="Teléfono Móvil" type="tel" form={form} />
            {clientPreferences.enableUsersNationalId && (
              <Input name="nationalId" placeholder="11111111-1" label="RUT" form={form} />
            )}
            {!noPasswordRoles.has(form.getValue('role')) && (
              <>
                <Input
                  name="password"
                  label="Contraseña"
                  placeholder="•••••••••"
                  type="password"
                  skipSubmit
                  readOnly={readOnly}
                  form={form}
                />

                <Input
                  name="password_confirm"
                  label="Repetir contraseña"
                  placeholder="•••••••••"
                  type="password"
                  skipSubmit
                  readOnly={readOnly}
                  form={form}
                />
              </>
            )}
          </div>
          {clientMedicalEnabled && form.getValue('role') === roles('noAccess') && (
            <div className={css.formPanel}>
              <h3 className={css.panelTitle}>Datos médicos adicionales</h3>
              {userPreferenceOptions
                .filter((option) => !option.isMedical || clientMedicalEnabled)
                .map((option) => (
                  <div key={option.name} className={css.extraItem}>
                    {option.type === 'toggle' ? (
                      <Toggle
                        name={['preferences', option.name]}
                        label={option.label}
                        form={form}
                      />
                    ) : (
                      <Input
                        name={['preferences', option.name]}
                        label={option.label}
                        placeholder={option.placeholder}
                        form={form}
                      />
                    )}
                    {option.note && <p className={css.infoText}>{option.note}</p>}
                  </div>
                ))}
            </div>
          )}
        </section>
      </div>
    </main>
  );
};

export default UserNew;
